import {BoardDimension, PayBoardConfig} from '@gongxigames/framework/slot/types/InfoPageConfig';

interface Params {
    width: number;
    height: number;
    padding: Point;
    scale: number;
    lineWidth: number;
    xSpaceBetweenElements: number;
    ySpaceBetweenElements: number;
    colors: {
        symbolCell: string;
        emptyCell: string;
    };
    board: {reels: PayBoardConfig; dimension: BoardDimension;};
}

export interface LinesParams extends Params {
    font: {
        size: number;
        family: string;
    };
    elementsOnRow: number;
    labelDistance: number;
    lines: Matrix[];
}

export interface CascadeParams extends Params {
    offsetX: number;
    check: {
        colors: Record<CheckKey, string>;
        cap: CanvasLineCap;
        size: number;
        distance: number;
        lineWidth: number;
    };
    ways: {
        win: Matrix;
    };
}

export interface WaysParams extends CascadeParams {
    ways: {
        win: Matrix;
        lose: Matrix;
    };
}

type Matrix = number[][];

interface Point {
    x: number;
    y: number;
}

interface Dimension {
    width: number;
    height: number;
}

interface Transform {
    x: number;
    y: number;
    width: number;
    height: number;
}

type CheckKey = 'win' | 'lose';

abstract class PayGenerator<P extends Params> {
    protected canvas!: HTMLCanvasElement;
    protected ctx!: CanvasRenderingContext2D;

    public constructor(protected readonly params: P) {
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d')!;
    }

    protected createCanvas(): void {
        this.init();

        const {x, y, width, height} = this.getTransform();

        this.canvas.width = width;
        this.canvas.height = height;
        this.ctx.translate(x, y);
    }

    protected drawBoard(board: Params['board'], actives: Matrix, pos: Point): void {
        const {colors, width, height} = this.params;

        for (let reel = 0; reel < board.reels.length; reel++) {
            const {size, shift} = board.reels[reel];

            for (let row = 0; row < size; row++) {
                const x = pos.x + reel * width;
                const y = pos.y + row * height + shift * height;
                const color = actives.find(el => el[0] === reel && el[1] === row) ? colors.symbolCell : colors.emptyCell;

                this.drawCube(x, y, color);
            }
        }
    }

    private drawCube(x: number, y: number, color: string): void {
        const {width, height,scale, lineWidth} = this.params;

        this.ctx.beginPath();
        this.ctx.fillStyle = color;
        this.ctx.rect(x * scale, y * scale, width * scale, height * scale);
        this.ctx.fill();
        this.ctx.lineWidth = lineWidth * scale;
        this.ctx.strokeStyle = '#000000';
        this.ctx.stroke();
        this.ctx.closePath();
    }

    protected abstract init(): void;
    protected abstract getTransform(): Transform;
}

export class PayLinesGenerator extends PayGenerator<LinesParams> {
    private boardDimension!: Dimension;
    private labelDimension!: Dimension;
    private totalTransform!: Transform;

    public generatePayLines(): HTMLCanvasElement {
        this.createCanvas();

        const {board, lines} = this.params;

        for (let i = 0; i < lines.length; i++) {
            const boardPos = this.getBoardPosition(i);
            const labelPos = this.getLabelPosition(boardPos);

            this.drawBoard(board, lines[i], boardPos);
            this.drawLabel(i + 1, labelPos);
        }

        return this.canvas;
    }

    protected init(): void {
        this.boardDimension = this.calculateBoardDimension();
        this.labelDimension = this.calculateLabelDimension();
        this.totalTransform = this.calculateTransform();
    }

    protected getTransform(): Transform {
        return this.totalTransform;
    }

    private calculateBoardDimension(): Dimension {
        const {width, height, board} = this.params;
        const {rows, columns} = board.dimension;

        const w = columns * width;
        const h = rows * height;

        return {width: w,height: h};
    }

    private calculateLabelDimension(): Dimension {
        const {lines} = this.params;

        return this.getTextDimension(lines.length.toString());
    }

    private calculateTransform(): Transform {
        const {scale, elementsOnRow, lines, padding, ySpaceBetweenElements, xSpaceBetweenElements, labelDistance} = this.params;
        const {width: bw, height: bh} = this.boardDimension;
        const {width: lw} = this.labelDimension;

        const countCols = elementsOnRow;
        const countRows = Math.ceil(lines.length / elementsOnRow);
        const textOffset = lw + labelDistance;

        const x = (textOffset + padding.x * 0.5) * scale;
        const y = padding.y * 0.5 * scale;
        const w = (countCols * bw + (countCols - 1) * xSpaceBetweenElements + padding.x + textOffset) * scale;
        const h = (countRows * bh + (countRows - 1) * ySpaceBetweenElements + padding.y) * scale;

        return {x, y, width : w, height: h};
    }

    private getBoardPosition(index: number): Point {
        const {elementsOnRow, xSpaceBetweenElements, ySpaceBetweenElements} = this.params;
        const {width, height} = this.boardDimension;

        const row = index / elementsOnRow << 0;
        const col = index % elementsOnRow;

        const x = col * (width + xSpaceBetweenElements);
        const y = row * (height + ySpaceBetweenElements);

        return {x, y};
    }

    private getLabelPosition(boardPos: Point): Point {
        const {labelDistance, lineWidth} = this.params;
        const {height} = this.boardDimension;

        const x = boardPos.x + lineWidth * 0.5 - labelDistance;
        const y = boardPos.y + height * 0.5 + lineWidth;

        return {x, y};
    }

    private drawLabel(num: number, pos: Point): void {
        const {scale, font} = this.params;

        this.ctx.textBaseline = 'middle';
        this.ctx.textAlign = 'right';
        this.ctx.font = `${font.size * scale}px ${font.family}`;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillText(num.toString(), pos.x * scale, pos.y * scale);
    }

    private getTextDimension(text: string): Dimension {
        const {font} = this.params;

        this.ctx.font = `${font.size}px ${font.family}`;

        const width = this.ctx.measureText(text).width;
        const height = font.size;

        return {width, height};
    }
}

export class PayCascadeGenerator<T extends CascadeParams> extends PayGenerator<T> {
    private boardDimension!: Dimension;
    private checkDimension!: Dimension;
    private totalTransform!: Transform;
    private ways!: Map<CheckKey, Matrix>;

    public generatePayWays(): HTMLCanvasElement {
        this.createCanvas();

        const {board} = this.params;
        const waysArr = Array.from(this.ways.entries());

        for (let i = 0; i < waysArr.length; i++) {
            const {0: key, 1: ways} = waysArr[i];

            const boardPos = this.getBoardPosition(i);
            const checkPos = this.getChecksPosition(boardPos);

            this.drawBoard(board, ways, boardPos);
            this.drawCheck(key, checkPos);
        }

        return this.canvas;
    }

    protected init(): void {
        this.ways = this.createWaysMap();
        this.boardDimension = this.calculateBoardDimension();
        this.checkDimension = this.calculateCheckDimension();
        this.totalTransform = this.calculateTransform();
    }

    protected getTransform(): Transform {
        return this.totalTransform;
    }

    protected createWaysMap(): Map<CheckKey, Matrix> {
        const {ways} = this.params;

        const waysMap = new Map();

        waysMap.set('win', ways.win);

        return waysMap;
    }

    private calculateBoardDimension(): Dimension {
        const {width, height, board} = this.params;
        const {rows, columns} = board.dimension;

        const w = columns * width;
        const h = rows * height;

        return {width: w, height: h};
    }

    private calculateCheckDimension(): Dimension {
        const {check} = this.params;

        return {width: check.size, height: check.size};
    }

    protected calculateTransform(): Transform {
        const {scale, padding, offsetX, xSpaceBetweenElements} = this.params;
        const {width: bw, height: bh} = this.boardDimension;
        const elementsOnRow = this.ways.size;

        const y = padding.y * 0.5 * scale;
        const x = (padding.x * 0.5 + offsetX) * scale;
        const w = (elementsOnRow * bw + ((elementsOnRow - 1) * xSpaceBetweenElements) + padding.x) * scale;
        const h = (bh + padding.y) * scale;

        return {x, y, width: w, height: h};
    }

    private getBoardPosition(index: number): Point {
        const {xSpaceBetweenElements} = this.params;
        const {width} = this.boardDimension;

        const x = index * (width + xSpaceBetweenElements);
        const y = 0;

        return {x, y};
    }

    private getChecksPosition(boardPos: Point): Point {
        const {scale, check} = this.params;
        const {width: bw, height: bh} = this.boardDimension;
        const {height: ch} = this.checkDimension;

        const x = (boardPos.x + bw + check.distance) * scale;
        const y = (boardPos.y + bh - ch) * scale;

        return {x, y};
    }

    private drawCheck(key: CheckKey, {x, y}: Point): void {
        const {check, scale} = this.params;
        const {width: cw, height: ch} = this.checkDimension;

        const lw = check.lineWidth * scale;
        const w = cw * scale;
        const h = ch * scale;

        this.ctx.strokeStyle = check.colors[key];
        this.ctx.lineCap = check.cap;
        this.ctx.lineWidth = lw;
        this.ctx.lineJoin = check.cap === 'round' ? 'round': 'miter';

        const path = new Path2D();

        switch(key) {
            case 'win':
                path.lineTo(x + lw, y + h * 0.5);
                path.lineTo(x + w * 0.4, y + h - lw);
                path.lineTo(x + w - lw, y + lw);
                break;

            case 'lose':
                path.lineTo(x + lw, y + lw);
                path.lineTo(x + w - lw, y + h - lw);
                path.moveTo(x + w - lw, y + lw);
                path.lineTo(x + lw, y + h - lw);
                break;
            default:
                break;
        }

        this.ctx.beginPath();
        this.ctx.stroke(path);
        this.ctx.closePath();
    }
}

export class PayWaysGenerator extends PayCascadeGenerator<WaysParams> {
    protected createWaysMap(): Map<CheckKey, Matrix> {
        const {ways} = this.params;

        const waysMap = new Map();

        waysMap.set('win', ways.win);
        waysMap.set('lose', ways.lose);

        return waysMap;
    }
}